/**
 * `useGameEffects` — the browser-side {@link EffectContext} that backs the client
 * effect registry. It turns the registry's abstract capabilities into real app
 * behavior:
 *  - **setBanner** → a piece of React state rendered by {@link DirectiveOverlay}, in the
 *    banner's tone (OMNINET band, ALERT hazard band, NARRATION letterbox);
 *  - **fireTransient** → a one-shot `{ effect, payload }` record that mounts an overlay for
 *    the effect's lifetime, then clears. The payload rides along so an FX can carry an
 *    opaque detail; each renderer narrows it itself and degrades to its content-free form
 *    when it doesn't recognise the shape. While a cue with a screen motion is live, the app
 *    root (`#root`) also wears `lvtt-fx-screen-<effect>`, so the whole screen jolts, punches or
 *    shakes under the overlay.
 *
 * The registry + hook stay framework-agnostic (the registry never imports React);
 * this is where React specifics live. A page wires it by passing the returned
 * `effects` to `useGameSession({ effects })` and rendering `<DirectiveOverlay state={…}>`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { bannerTone } from "@lancer-vtt/shared";
import type { BannerSpec, EffectContext } from "./effectRegistry";
import { SweepBanner } from "../SweepBanner";

/**
 * The transient FX currently flashing: its registry `effect` name plus the **opaque
 * payload** the directive carried.
 */
export type TransientFx = { effect: string; payload: unknown };

export type DirectiveOverlayState = {
  banner: BannerSpec | null;
  /** The transient FX currently flashing (e.g. `{ effect: "glitch" }`), or null. */
  transient: TransientFx | null;
}

export type GameEffects = {
  effects: EffectContext;
  overlay: DirectiveOverlayState;
}

/** How long a transient FX stays mounted when its effect isn't listed below — one beat. */
const FX_MS_DEFAULT = 600;

/**
 * Per-effect lifetimes. **Keep each entry in step with its CSS animation duration**
 * (`index.css`, `lvtt-fx-*`): the timer unmounts the element and drops the screen class, so a
 * longer animation is simply truncated.
 */
const FX_MS: Record<string, number> = { "full-repair": 2400, glitch: 700, impact: 500, shake: 450 };

/** The cues that move the whole screen (`index.css`, `lvtt-fx-screen-*`). */
const SCREEN_FX = new Set(["glitch", "impact", "shake"]);

/** Build the browser effect context + the overlay state it drives. */
export function useGameEffects(): GameEffects {
  const [banner, setBanner] = useState<BannerSpec | null>(null);
  const [transient, setTransient] = useState<TransientFx | null>(null);
  const transientTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fireTransient = useCallback((effect: string, payload?: unknown) => {
    if (transientTimer.current) clearTimeout(transientTimer.current);
    setTransient({ effect, payload });
    // A one-shot: clear after its lifetime so it never persists (ephemeral).
    transientTimer.current = setTimeout(() => setTransient(null), FX_MS[effect] ?? FX_MS_DEFAULT);
  }, []);

  useScreenMotion(transient);

  const effects = useMemo<EffectContext>(() => ({ setBanner, fireTransient }), [fireTransient]);

  return { effects, overlay: { banner, transient } };
}

/** The `full-repair` FX's subtitle, when its payload carries one. */
function fullRepairSub(payload: unknown): string | undefined {
  const sub = (payload as { sub?: unknown } | undefined)?.sub;
  return typeof sub === "string" ? sub : undefined;
}

/**
 * Put `lvtt-fx-screen-<effect>` on the app root while a screen-moving cue is live. A new cue
 * (even the same one again) is a new `transient` object, so the class comes off and goes back on
 * after a reflow and the animation restarts.
 */
function useScreenMotion(transient: TransientFx | null): void {
  useEffect(() => {
    const effect = transient?.effect;
    if (effect === undefined || !SCREEN_FX.has(effect)) return;
    const root = document.getElementById("root");
    if (root === null) return;
    const cls = `lvtt-fx-screen-${effect}`;
    root.classList.remove(cls);
    void root.offsetWidth;
    root.classList.add(cls);
    return () => root.classList.remove(cls);
  }, [transient]);
}

/**
 * Renders the standing banner and any flashing transient FX. Mount it once near the
 * app root, fed the `overlay` state from {@link useGameEffects}.
 */
export function DirectiveOverlay({ state }: { state: DirectiveOverlayState }) {
  if (state.banner === null && state.transient === null) return null;
  const fx = state.transient?.effect ?? null;
  return (
    <>
      {state.banner && <DirectiveBannerView banner={state.banner} />}
      {/* glitch: three tear bands invert what's under them, a chroma wash, a terminal line. */}
      {fx === "glitch" && (
        <div className="lvtt-directive-overlay lvtt-fx-glitch" aria-hidden>
          <div className="lvtt-fx-tear lvtt-fx-tear-a" />
          <div className="lvtt-fx-tear lvtt-fx-tear-b" />
          <div className="lvtt-fx-tear lvtt-fx-tear-c" />
          <div className="lvtt-fx-chroma" />
          <div className="lvtt-fx-sig">SIGNAL // RESYNC</div>
        </div>
      )}
      {/* full-repair (ephemeral): the FULL REPAIR sweep (SweepBanner, `.lvtt-fx-fullrepair` in index.css). */}
      {fx === "full-repair" && <SweepBanner className="lvtt-fx-fullrepair" testId="full-repair-banner" text="FULL REPAIR" sub={fullRepairSub(state.transient?.payload)} />}
      {/* impact: a hot-white bloom that cools through GMS red (the root punches under it). */}
      {fx === "impact" && <div className="lvtt-directive-overlay lvtt-fx-impact" aria-hidden />}
      {/* shake: the root carries the motion; this frame only marks the cue for tests and AT. */}
      {fx === "shake" && <div className="lvtt-directive-overlay lvtt-fx-shake" aria-hidden />}
    </>
  );
}

/** The standing banner in its tone. Never takes clicks: the board under it stays playable. */
function DirectiveBannerView({ banner }: { banner: BannerSpec }) {
  const tone = bannerTone(banner);
  const img = banner.image ? <img src={banner.image} alt="" className="lvtt-directive-banner-img" /> : null;
  if (tone === "alert") {
    return (
      <div className="lvtt-directive-banner lvtt-banner-alert" role="status" data-tone={tone}>
        <div className="lvtt-banner-hazard" />
        <div className="lvtt-banner-alert-text">
          {img}
          <p className="lvtt-directive-banner-text">{banner.text}</p>
        </div>
        <div className="lvtt-banner-hazard" />
      </div>
    );
  }
  if (tone === "narration") {
    return (
      <div className="lvtt-directive-banner lvtt-banner-narration" role="status" data-tone={tone}>
        <div className="lvtt-banner-lb-top" />
        <div className="lvtt-banner-lb-bot">
          {img}
          <p className="lvtt-directive-banner-text lvtt-banner-lb-line">{banner.text}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="lvtt-directive-banner lvtt-banner-omninet" role="status" data-tone={tone}>
      <div className="lvtt-banner-omni-band">
        <div className="lvtt-banner-omni-tag">
          <span>OMNINET</span>
          <span className="lvtt-banner-omni-sub">// TX · GM</span>
        </div>
        {img}
        <p className="lvtt-directive-banner-text lvtt-banner-omni-text">{banner.text}</p>
      </div>
    </div>
  );
}
